import React, { useEffect, Fragment, useState, useRef } from "react";
import { forEach, head, map, isEmpty, find, get, cloneDeep, isUndefined, isNull, includes } from "lodash";
import Plotly from "plotly.js-basic-dist-min";
import createPlotlyComponent from "react-plotly.js/factory";
import { Table, Drawer, Button, Popover, Tooltip, Checkbox, notification, Badge, Modal } from "antd";

// import local dependencies
import "./populationInsight.scss";
import Icon from "../../../../assets/icon_config";
import MillenniumLogo from "@assets/images/millenniumLogo.png";
import { tableColumns, sideCardTableColumn } from "./json/populationInsightTable";
import Filters from "@components/filters";
import { DateRangePreloader, DateRange } from "@components/dateRange";
import dashboardReport from "../../../../assets/images/dashboardReport.png";
import { Reasons } from "./reasons";
import { Percentage } from "./percentage";
import { PercentageNormal } from "./percentageNormal";
import DashboardService from "@services/dashboard.service";
import { usePopulationInsightStore } from "@stores/populationInsightStore";
import { arrayNullAndEmptyValidation, milleniumClientCheck } from "@utils/helpers";
import { CloseCircleOutlined } from "@ant-design/icons";
import Chart from "react-apexcharts";
import { getClientName } from "@utils/helpers";
import { TableLocale } from "@components/noDataFound";

// make constants
const Plot = createPlotlyComponent(Plotly);

// **********************************************************

type Color = string[];
interface Props {
  colorOfBrand: Color;
}

// **********************************************************

const PopulationInsight = (props: Props) => {
  // getting state from the store
  const _filtersData = usePopulationInsightStore((state) => state.filtersData);
  const _setFiltersData = usePopulationInsightStore((state) => state.setFiltersData);
  const _activeSideCardIndex = usePopulationInsightStore((state) => state.activeSideCardIndex);
  const _setActiveSideCardIndex = usePopulationInsightStore((state) => state.setActiveSideCardIndex);
  const _rangeStartDate = usePopulationInsightStore((state) => state.rangeStartDate);
  const _setRangeStartDate = usePopulationInsightStore((state) => state.setRangeStartDate);
  const _rangeEndDate = usePopulationInsightStore((state) => state.rangeEndDate);
  const _setRangeEndDate = usePopulationInsightStore((state) => state.setRangeEndDate);
  const _selectedFilterOptions = usePopulationInsightStore((state) => state.selectedFilterOptions);
  const _setSelectedFilterOptions = usePopulationInsightStore((state) => state.setSelectedFilterOptions);
  const _stateInfo = usePopulationInsightStore((state) => state.stateInfo);
  const _setStateInfo = usePopulationInsightStore((state) => state.setStateInfo);
  const _comingFromMembers = usePopulationInsightStore((state) => state.comingFromMembers);
  const [_yearMonth, _setYearMonth] = useState<string>("");
  const [lastMonthCost, setLastMonthCost] = useState<number>();
  const [_cardYearMonth, _setCardYearMonth] = useState<string>("");
  const [_appliedFilters, _setAppliedFilters] = useState<any>([]);
  const [displayMessage, setDisplayMessage] = useState<boolean>(false);
  const _filteryearMonth = useRef<any>({});
  /* -------------------------------------------- */

  // initial round of setting the _stateInfo
  useEffect(() => {
    console.log("PopulationInsight.useEffect");
    if (!_comingFromMembers) {
      DashboardService.fetchPopulationInsightsReportsInfo().subscribe({
        next: (v) => {
          const response = get(v, "data.data.result", {});
          _setYearMonth(get(response, "data.dateRange.endDate", undefined));
          setLastMonthCost(get(response, "data.topFilterCardInfo[0].value", undefined));
          _setCardYearMonth(get(response, "data.dateRange.endDate", undefined));
          const data = cloneDeep(get(response, "data.kpiDataInfo", []));
          if (Object.keys(_filteryearMonth.current).length === 0) {
            _filteryearMonth.current.startDate = get(response, "data.dateRange.startDate", undefined);
            _filteryearMonth.current.endDate = get(response, "data.dateRange.endDate", undefined);
          }
          //  API call will be here
          const initialSideCards: any = [];

          forEach(data, (obj) => {
            const sideCardInfo: any = obj.sideCardInfo;
            sideCardInfo["areaChart"] = obj.lineGraphData;
            initialSideCards.push(sideCardInfo);
          });

          const activeCardInfo = head(data);

          // setting the _stateInfo after getting the response
          _setStateInfo({
            isLoading: false,
            sideCardData: initialSideCards,
            topCardData: get(response, "data.topFilterCardInfo", []),
            lineGraphInfo: get(activeCardInfo, "lineGraphData", []),
            barGraphInfo: {},
            tableGraphInfo: get(response, "data.tableData", []),
            isOpenDrawer: false,
            selectedRowIndex: null,
            isPopOverOpen: false,
            isComparePopOverOpen: false,
            currentGraphType: "scatter",
            multiLineGraphInfo: [],
            graphConfig: {},
            selectedGraphOptions: [],
            compareOnValue: {},
            popoverConfig: null,
            selectedData: null,
            response: response,
            riskAdjustedData: get(activeCardInfo, "riskAdjustedInfo", {}),
            persistentMemberInfo: get(activeCardInfo, "persistentMemberInfo", {}),
            riskAndPersistentMemberInfo: get(activeCardInfo, "riskAndPersistentMemberInfo", {}),
            dateChangeFilterApplied: false,
            explainabilityInfo: get(activeCardInfo, "explainabilityInfo", []),
            comparisonCategoryPopVisibility: false,
          });

          // updating the store
          _setRangeStartDate(get(response, "data.dateRange.startDate", undefined));
          _setRangeEndDate(get(response, "data.dateRange.endDate", undefined));
          _setActiveSideCardIndex(isUndefined(_activeSideCardIndex) ? 0 : _activeSideCardIndex);
          _setFiltersData(cloneDeep(get(response, "options.sideFilters", {})));
        },
        error: (e) => {
          console.log(e);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* -------------------------------------------- */
  // handle click on the side card
  const handleSideCardClick = (index: number) => {
    const data = cloneDeep(get(_stateInfo.response, "data.kpiDataInfo", []));

    const activeCardInfo = cloneDeep(data[index]);

    const initialSideCards: any = [];

    forEach(data, (obj) => {
      const sideCardInfo: any = obj.sideCardInfo;
      sideCardInfo["areaChart"] = obj.lineGraphData;
      initialSideCards.push(sideCardInfo);
    });

    _setStateInfo({
      ..._stateInfo,
      lineGraphInfo: get(activeCardInfo, "lineGraphData", {}),
      tableGraphInfo: get(_stateInfo, "response.data.tableData", []),
      selectedRowIndex: null,
      sideCardData: initialSideCards,
      currentGraphType: "scatter",
      selectedGraphOptions: [],
      riskAdjustedData: get(activeCardInfo, "riskAdjustedInfo", {}),
      persistentMemberInfo: get(activeCardInfo, "persistentMemberInfo", {}),
      riskAndPersistentMemberInfo: get(activeCardInfo, "riskAndPersistentMemberInfo", {}),
      explainabilityInfo: get(activeCardInfo, "explainabilityInfo", []),
      compareOnValue: {
        ..._stateInfo.compareOnValue,
        options: [],
      },
    });

    _setActiveSideCardIndex(index);
  };

  const handleCompareClick = (compareOnValue: any) => {
    const data = cloneDeep(get(_stateInfo.response, "data.kpiDataInfo", []));

    const activeSideCard = get(_stateInfo, "sideCardData", [])[_activeSideCardIndex ? _activeSideCardIndex : 0];

    const activeCardInfo = find(data, (item) => {
      return item.sideCardInfo.title === activeSideCard.title;
    });
    _setStateInfo({
      ..._stateInfo,
      multiLineGraphInfo: map(get(activeCardInfo, "compareDataInfo", []), (item) => {
        if (item.type === compareOnValue.value && compareOnValue.options.includes(item.subType)) {
          return item;
        }
      }),
      currentGraphType: "multiLineGraph",
      selectedGraphOptions: [],
      isComparePopOverOpen: false,
    });
  };

  /* -------------------------------------------- */

  // handle click on a single row of the table
  const handleTableRowClick = (record: any, rowIndex: any) => {
    const data = cloneDeep(get(_stateInfo.response, "data.kpiDataInfo", []));

    // find all the info the active card from final JSON
    const activeCardInfo = data[_activeSideCardIndex ? _activeSideCardIndex : 0];

    // get index of the clicked row
    const index = get(activeCardInfo, "lineGraphData.monthYear", []).findIndex((item: any) => item === record.yearMonth);

    // use the index to get details of the line graph
    const _lineGraphData = {
      monthYearLabel: [get(activeCardInfo, "lineGraphData.monthYearLabel", [])[index]],
      monthYear: [get(activeCardInfo, "lineGraphData.monthYear", [])[index]],
      currentMonthYearValues: [get(activeCardInfo, "lineGraphData.currentMonthYearValues", [])[index]],
      previousMonthYearValues: [get(activeCardInfo, "lineGraphData.previousMonthYearValues", [])[index]],
    };

    const _sideCardData: any = [];

    const tableRow = find(get(_stateInfo.response, "data.tableData", []), {
      yearMonth: record.yearMonth,
    });

    // change side cards date according to the row selected
    forEach(data, (obj) => {
      const sideCardInfo: any = obj.sideCardInfo;
      const _tableColumn = find(sideCardTableColumn, {
        title: sideCardInfo.title,
      });

      const _dateFilterForCards = [
        {
          date: get(tableRow, "previousYearData.yearMonth", null),
          value:
            sideCardInfo.title === sideCardTableColumn[0].title
              ? obj.lineGraphData.previousMonthYearValues[tableRow.id - 1]
              : get(tableRow, `previousYearData.${get(_tableColumn, "tableColumn", "")}`, null),
        },
        {
          date: get(tableRow, "yearMonth", null),
          value: sideCardInfo.title === sideCardTableColumn[0].title ? obj.lineGraphData.currentMonthYearValues[tableRow.id - 1] : get(tableRow, get(_tableColumn, "tableColumn", ""), null),
        },
      ];

      sideCardInfo["areaChart"] = {};
      sideCardInfo["dateFilter"] = _dateFilterForCards;

      _sideCardData.push(sideCardInfo);
    });

    _setStateInfo({
      ..._stateInfo,
      lineGraphInfo: _lineGraphData,
      selectedRowIndex: rowIndex,
      sideCardData: _sideCardData,
    });
  };

  /* -------------------------------------------- */

  // handle click on the marker of the line graph
  const handleMarkerSelect = (record: any) => {
    const data = cloneDeep(get(_stateInfo.response, "data.kpiDataInfo", []));

    const _activeSideCard = cloneDeep(data[_activeSideCardIndex ? _activeSideCardIndex : 0]);

    delete _activeSideCard["areaChart"];

    const tableRow = find(get(_stateInfo.response, "data.tableData", []), {
      yearMonth: record.x,
    });

    const _sideCardData: any = [];

    forEach(data, (obj) => {
      const sideCardInfo: any = obj.sideCardInfo;
      const _tableColumn = find(sideCardTableColumn, {
        title: sideCardInfo.title,
      });

      const _dateFilterForCards = [
        {
          date: get(tableRow, "previousYearData.yearMonth", null),
          value:
            sideCardInfo.title === sideCardTableColumn[0].title
              ? obj.lineGraphData.previousMonthYearValues[tableRow.id - 1]
              : get(tableRow, `previousYearData.${get(_tableColumn, "tableColumn", "")}`, null),
        },
        {
          date: get(tableRow, "yearMonth", null),
          value: sideCardInfo.title === sideCardTableColumn[0].title ? obj.lineGraphData.currentMonthYearValues[tableRow.id - 1] : get(tableRow, get(_tableColumn, "tableColumn", ""), null),
        },
      ];
      sideCardInfo["areaChart"] = {};
      sideCardInfo["dateFilter"] = _dateFilterForCards;

      _sideCardData.push(sideCardInfo);
    });

    _setStateInfo({
      ..._stateInfo,
      tableGraphInfo: [tableRow],
      sideCardData: _sideCardData,
    });
  };

  /* -------------------------------------------- */

  // function responsible to set the _stateInfo to the initial State( comes in action with double click on the screen )
  const setStateToInitial = ({ forcefullyChange = false }) => {
    const initialSideCards: any = [];
    const data = cloneDeep(get(_stateInfo.response, "data.kpiDataInfo", []));

    forEach(data, (obj) => {
      const sideCardInfo: any = obj.sideCardInfo;
      sideCardInfo["areaChart"] = obj.lineGraphData;
      initialSideCards.push(sideCardInfo);
    });

    const activeCardInfo = data[_activeSideCardIndex ? _activeSideCardIndex : 0];

    _setStateInfo({
      ..._stateInfo,
      tableGraphInfo: get(_stateInfo.response, "data.tableData", []),
      lineGraphInfo: get(activeCardInfo, "lineGraphData", []),
      selectedRowIndex: null,
      sideCardData: initialSideCards,
      currentGraphType: "scatter",
      selectedGraphOptions: [],
      compareOnValue: {},
      explainabilityInfo: get(activeCardInfo, "explainabilityInfo", []),
    });
  };

  /* ----------------------------------------------- */

  const applySideFilters = (startDate: any | undefined, endDate: any | undefined, comingFromDateChange?: boolean | undefined) => {
    const _startDate = comingFromDateChange ? startDate : _rangeStartDate;
    const _endDate = comingFromDateChange ? endDate : _rangeEndDate;

    const filters: any = [];

    forEach(_filtersData, (item) => {
      if (item.selectedOptions !== "NA" && arrayNullAndEmptyValidation(item.selectedOptions, item.filterType)) {
        filters.push({
          filterType: item.filterName,
          filterValues: item.selectedOptions,
        });
      }
    });

    if (isEmpty(filters)) {
      _setSelectedFilterOptions(filters);

      _setStateInfo({
        ..._stateInfo,
        isLoading: true,
        isOpenDrawer: false,
        dateChangeFilterApplied: true,
      });

      handleDateChange([_startDate, _endDate], true, filters);
    } else {
      // find out if user has chosen anything from the mandatory 6 filters
      // const mandatoryFilters = filter(filters, (item) => {
      //   return includes(["CHRONIC", "CLINIC", "PAYER", "PCP NAME", "PLAN", "ZIP CODE"], get(item, "filterType", null));
      // });

      // if yes, then call the API
      //if (mandatoryFilters.length > 0) {
      _setSelectedFilterOptions(filters);

      _setStateInfo({
        ..._stateInfo,
        isLoading: true,
        isOpenDrawer: false,
        dateChangeFilterApplied: true,
      });

      DashboardService.fetchPopulationInsightsReportsInfoByFilters({ filters: filters }, _startDate, _endDate, _yearMonth).subscribe({
        next: (v) => {
          const response = get(v, "data.data.result", {});
          const data = cloneDeep(get(response, "data.kpiDataInfo", []));
          const initialSideCards: any = [];
          const _compareOptions: any = [];

          forEach(data, (obj, i: number) => {
            const sideCardInfo: any = obj.sideCardInfo;
            sideCardInfo["areaChart"] = obj.lineGraphData;
            initialSideCards.push(sideCardInfo);

            if (i === 1) {
              forEach(obj.compareDataInfo, (item) => {
                const _item: any = {};
                const val = find(_compareOptions, { title: item.type });

                if (!isUndefined(val)) {
                  val["options"] = [...val["options"], { title: item.subType, value: item.subType, colorFlag: item.currentMonthYearValues.every((element: any) => element === null) }];
                } else {
                  _item["title"] = item.type;
                  _item["value"] = item.type;
                  _item["options"] = [{ title: item.subType, value: item.subType, colorFlag: item.currentMonthYearValues.every((element: any) => element === null) }];
                  _compareOptions.push(_item);
                }
              });
            }
          });

          const activeCardInfo = data[_activeSideCardIndex ? _activeSideCardIndex : 0];

          let _topCard = [];

          if (_startDate !== _filteryearMonth.current.startDate || _endDate !== _filteryearMonth.current.endDate) {
            setLastMonthCost(get(response, "data.topFilterCardInfo[0].value", undefined));
            _topCard.push(...get(response, "data.topFilterCardInfo", []));
          } else {
            _topCard.push(..._stateInfo.topCardData);
          }

          // _topCard = get(response, "data.topFilterCardInfo", []);
          // if (_stateInfo.topCardData.length === 3) {
          //   _topCard.push(_stateInfo.topCardData[1]);
          //   _topCard.push(_stateInfo.topCardData[2]);
          // } else if (_stateInfo.topCardData.length === 2) {
          //   _topCard.push(_stateInfo.topCardData[0]);
          //   _topCard.push(_stateInfo.topCardData[1]);
          // }

          // setting the _stateInfo after getting the response
          _setStateInfo({
            ..._stateInfo,
            isLoading: false,
            sideCardData: initialSideCards,
            topCardData: _topCard,
            lineGraphInfo: get(activeCardInfo, "lineGraphData", []),
            explainabilityInfo: get(activeCardInfo, "explainabilityInfo", []),
            barGraphInfo: {},
            tableGraphInfo: get(response, "data.tableData", []),
            isOpenDrawer: false,
            selectedRowIndex: null,
            isPopOverOpen: false,
            isComparePopOverOpen: false,
            currentGraphType: "scatter",
            multiLineGraphInfo: [],
            graphConfig: {},
            selectedGraphOptions: [],
            compareOnValue: {},
            popoverConfig: null,
            selectedData: null,
            response: response,
            riskAdjustedData: get(activeCardInfo, "riskAdjustedInfo", {}),
            persistentMemberInfo: get(activeCardInfo, "persistentMemberInfo", {}),
            riskAndPersistentMemberInfo: get(activeCardInfo, "riskAndPersistentMemberInfo", {}),
            compareOptions: _compareOptions,
            isNoDataFoundModalVisible: response === "No Data Found",
          });

          // _setActiveSideCardIndex(initialSideCards[0]);
        },
        error: (e) => {
          console.log(e);
          _setStateInfo({
            ..._stateInfo,
            isLoading: false,
          });
        },
      });
      //}
      // else {
      //   // else show error to choose at least one mandatory filter
      //   notification.error({
      //     message: "Error",
      //     description: "Please choose filters from CHRONIC, PAYER, PCP NAME, CLINIC, ZIP CODE and PLAN.",
      //   });
      // }
    }
  };

  /* -------------------------------------------- */

  const filtersChangeHandler = (category: string, selectedArray: any) => {
    let copyFilterData: any = cloneDeep(_filtersData);

    // if selected array is empty for a category, delete the key itself
    isEmpty(selectedArray) ? delete copyFilterData[category]["selectedOptions"] : (copyFilterData[category].selectedOptions = Array.isArray(selectedArray) ? [...selectedArray] : selectedArray);
    let appliedFilters: any = [];	
    copyFilterData.forEach((fi: any) =>	
      fi?.selectedOptions?.length > 0	
        ? appliedFilters.push({ filterName: fi.filterName, selectedOptions: typeof fi.selectedOptions === "string" ? new Array(fi.selectedOptions) : fi.selectedOptions })	
        : null	
    );	
    _setAppliedFilters(appliedFilters);
    _setFiltersData(copyFilterData);
    setDisplayMessage(true);
    
  };

  /* -------------------------------------------- */

  const clearFilterHandler = () => {
    _setFiltersData(
      map(_filtersData, (item) => {
        item.filterType === "radio" ? (item.selectedOptions = "NA") : delete item.selectedOptions;
        return item;
      })
    );
    setDisplayMessage(true);	
    _setAppliedFilters([]);
  };

  /* -------------------------------------------- */

  const handleDateChange = (range: any, comingFromFilters: any, filters: any = _selectedFilterOptions) => {
    let startDate = range[0];
    let endDate = range[1];

    _setRangeStartDate(range[0]);
    _setRangeEndDate(range[1]);
    _setCardYearMonth(endDate);

    _setStateInfo({
      ..._stateInfo,
      isLoading: true,
      dateChangeFilterApplied: true,
    });

    if (!isEmpty(filters) && comingFromFilters !== true) {
      applySideFilters(startDate, endDate, true);
    } else {
      DashboardService.fetchPopulationInsightsReportsInfoByDateRange(startDate, endDate, _yearMonth).subscribe({
        next: (v) => {
          const response = get(v, "data.data.result", {});
          setLastMonthCost(get(response, "data.topFilterCardInfo[0].value", undefined));

          const data = cloneDeep(get(response, "data.kpiDataInfo", []));

          //  API call will be here
          const initialSideCards: any = [];

          forEach(data, (obj) => {
            const sideCardInfo: any = obj.sideCardInfo;
            sideCardInfo["areaChart"] = obj.lineGraphData;
            initialSideCards.push(sideCardInfo);
          });

          const activeCardInfo = data[_activeSideCardIndex ? _activeSideCardIndex : 0];

          // setting the _stateInfo after getting the response
          _setStateInfo({
            ..._stateInfo,
            isLoading: false,
            sideCardData: initialSideCards,
            topCardData: get(response, "data.topFilterCardInfo", []),
            lineGraphInfo: get(activeCardInfo, "lineGraphData", []),
            barGraphInfo: {},
            tableGraphInfo: get(response, "data.tableData", []),
            isOpenDrawer: false,
            selectedRowIndex: null,
            isPopOverOpen: false,
            isComparePopOverOpen: false,
            currentGraphType: "scatter",
            multiLineGraphInfo: [],
            graphConfig: {},
            selectedGraphOptions: [],
            compareOnValue: {},
            popoverConfig: null,
            selectedData: null,
            response: response,
            riskAdjustedData: get(activeCardInfo, "riskAdjustedInfo", {}),
            persistentMemberInfo: get(activeCardInfo, "persistentMemberInfo", {}),
            riskAndPersistentMemberInfo: get(activeCardInfo, "riskAndPersistentMemberInfo", {}),
            explainabilityInfo: get(activeCardInfo, "explainabilityInfo", []),
            compareOptions: [],
          });

          _setFiltersData(cloneDeep(get(response, "options.sideFilters", {})));
        },
        error: (e) => {
          console.log(e);
        },
      });
    }
  };

  const handleFilterFromPopover = (filterName: any) => {	
    const index = _filtersData.findIndex((filter: any) => filter.filterName === filterName);	
    if (index >= 0) filtersChangeHandler(index, []);	
  };	
  const appliedFilters = (filters: any) => {	
    return (	
      <div className="applied_filter_popover custom-scroll-bar">	
        {displayMessage && (	
          <div style={{ paddingBottom: "1rem" }}>	
            Please click on the <b>Apply Filter</b> in filter section.	
          </div>	
        )}	
        {filters?.length > 0 	
          ? filters.map((filter: any) => (            	
              <div className="applied_filter_popover_content">	
                <div className="applied_filter_popover_content__section-1">	
                  <div className="applied_filter_popover_content_label">	
                     {filter.filterName + `${filter.filterName === "TOTAL COST" || filter.filterName === "PREDICTED COST (in 12 months)" ? ` (` + filter.selectedOptions[0].filterType + `)` : ""} : `}	
                  </div>	
                </div>	
                <div className="applied_filter_popover_content__section-2">	
                  {filter?.selectedOptions?.map((option: any, index: number) => {	
                    return filter.filterName === "TOTAL COST" || filter.filterName === "PREDICTED COST (in 12 months)" ? (	
                      <span className="applied_filter_popover_content_value">{option.filterValues + (index !== filter?.selectedOptions.length - 1 ? ", " : "")}</span>	
                    ) : (	
                      <span className="applied_filter_popover_content_value">{option + (index !== filter?.selectedOptions.length - 1 ? ", " : "")}</span>	
                    );	
                  })}	
                </div>	
                <div className="applied_filter_popover_content__close">	
                  <CloseCircleOutlined	
                    className="applied_filter_popover_content_closedicon"	
                    onClick={() => {	
                      handleFilterFromPopover(filter.filterName);	
                      setDisplayMessage(true);	
                    }}	
                  />	
                </div>	
              </div>	
            ))	
          : null}	
      </div>	
    );	
  };

  // const labels = ['PAYER', 'PCP NAME', 'ZIP CODE', 'CLINIC', 'CHRONIC'];
  // const labels2 = ['HCC/RAF', 'TOTAL COST', 'PREDICTED COST(in 12 Months)', 'SDoH RISK', 'HOSPITAL UTILIZATION RISK']
  // const labels3 = ['HOSPITAL ADMISSION RISK', 'ER EVENT RISK', 'ER ADMISSION RISK', 'RE ADMISSION RISK']
  const clientName = getClientName();
    let headerLogo = clientName === 'Millennium' ? MillenniumLogo : dashboardReport;
    if (process.env.REACT_APP_ENVIRONMENT === "demo") {
      headerLogo = dashboardReport;
    }
  return (
    <>
      <div
        className="ui-population-insight"
        onDoubleClick={() => setStateToInitial({})}
        onClick={() => {
          if (_stateInfo.isPopOverOpen || _stateInfo.isComparePopOverOpen) {
            _setStateInfo({
              ..._stateInfo,
              isPopOverOpen: false,
              isComparePopOverOpen: false,
            });
          }
        }}
      >
        <div className="ui-population-insight__header">
          <div className="ui-population-insight__header__heading">
            <img src={headerLogo} alt="dashboardReport" className="ui-population-insight__header__heading__img" />
            <div className="ui-population-insight__header__heading__title">Population Insights</div>

            {/* {
              labels.map((item: any) => {
                return (
                  <div >
                    <div style={{ borderRadius: "30px" ,borderColor:"gray"}}>{item}: &#9432;</div>
                  </div>
                )
              })
            } */}

            {/* {
              labels2.map((item: any) => {
                return (
                  <div >
                    <div style={{ borderRadius: "30px" }}>{item}: &#9432;</div>
                  </div>
                )
              })
            } */}
          </div>
          {/* {
              labels3.map((item: any) => {
                return (
                  <div >
                  <div style={{borderRadius:"30px"}}>{item}: &#9432;</div>
                  </div>
                )
              })
            } */}
          {_appliedFilters.length > 0 || displayMessage ? (	
            <Popover placement="left" title={"Applied Filters"} content={<div>{appliedFilters(_appliedFilters)}</div>}>	
              <div className="ui-population-insight__header__filter">	
                {!_stateInfo.isLoading ? (	
                  <Badge dot={!isEmpty(_selectedFilterOptions)} offset={[-29, 12]}>	
                    <div	
                      className={`ui-population-insight__header__filter__button btn-icon`}	
                      style={{ padding: "9px" }}	
                      onClick={(e) => {	
                        e.stopPropagation();	
                        _setStateInfo({	
                          ..._stateInfo,	
                          isOpenDrawer: !_stateInfo.isOpenDrawer,	
                        });	
                      }}	
                    >	
                      <Icon name="filter" />	
                    </div>	
                  </Badge>	
                ) : _stateInfo.dateChangeFilterApplied ? (	
                  <Badge dot={!isEmpty(_selectedFilterOptions)} offset={[-29, 12]}>	
                    <div	
                      className={`ui-population-insight__header__filter__button btn-icon`}	
                      style={{ padding: "9px" }}	
                      onClick={(e) => {	
                        e.stopPropagation();	
                        _setStateInfo({	
                          ..._stateInfo,	
                          isOpenDrawer: !_stateInfo.isOpenDrawer,	
                        });	
                      }}	
                    >	
                      <Icon name="filter" />	
                    </div>	
                  </Badge>	
                ) : (	
                  <div className="ui-population-insight__header__filter__filter-icon-preloader">	
                    <div className="ui-population-insight__header__filter__filter-icon-preloader__title shimmerBG" />	
                  </div>	
                )}	
                {!_stateInfo.isLoading ? (	
                  <DateRange rangeStartDate={_rangeStartDate} rangeEndDate={_rangeEndDate} handleDateChange={handleDateChange} />	
                ) : _stateInfo.dateChangeFilterApplied ? (	
                  <DateRange rangeStartDate={_rangeStartDate} rangeEndDate={_rangeEndDate} handleDateChange={handleDateChange} />	
                ) : (	
                  <DateRangePreloader />	
                )}	
              </div>	
            </Popover>	
          ) : (
            <div className="ui-population-insight__header__filter">	
              {!_stateInfo.isLoading ? (	
                <Badge dot={!isEmpty(_selectedFilterOptions)} offset={[-29, 12]}>	
                  <div	
                    className={`ui-population-insight__header__filter__button btn-icon`}	
                    style={{ padding: "9px" }}	
                    onClick={(e) => {	
                      e.stopPropagation();	
                      _setStateInfo({	
                        ..._stateInfo,	
                        isOpenDrawer: !_stateInfo.isOpenDrawer,	
                      });	
                    }}	
                  >	
                    <Icon name="filter" />	
                  </div>	
                </Badge>	
              ) : _stateInfo.dateChangeFilterApplied ? (	
                <Badge dot={!isEmpty(_selectedFilterOptions)} offset={[-29, 12]}>	
                  <div	
                    className={`ui-population-insight__header__filter__button btn-icon`}	
                    style={{ padding: "9px" }}	
                    onClick={(e) => {	
                      e.stopPropagation();	
                      _setStateInfo({	
                        ..._stateInfo,	
                        isOpenDrawer: !_stateInfo.isOpenDrawer,	
                      });	
                    }}	
                  >	
                    <Icon name="filter" />	
                  </div>	
                </Badge>	
              ) : (	
                <div className="ui-population-insight__header__filter__filter-icon-preloader">	
                  <div className="ui-population-insight__header__filter__filter-icon-preloader__title shimmerBG" />	
                </div>	
              )}	
              {!_stateInfo.isLoading ? (	
                <DateRange rangeStartDate={_rangeStartDate} rangeEndDate={_rangeEndDate} handleDateChange={handleDateChange} />	
              ) : _stateInfo.dateChangeFilterApplied ? (	
                <DateRange rangeStartDate={_rangeStartDate} rangeEndDate={_rangeEndDate} handleDateChange={handleDateChange} />	
              ) : (	
                <DateRangePreloader />	
              )}	
            </div>	
          )}
        </div>

        <div className="ui-population-insight__filter">
          {!_stateInfo.isLoading ? (
            map(_stateInfo.topCardData, (item, index) => {
              return <Cards key={"card" + index} {...item} {...props} yearMonth={_yearMonth} cardYearMonth={_cardYearMonth} lastMonthCost={lastMonthCost} />;
            })
          ) : (
            <CardsPreloader />
          )}
        </div>

        <div className="ui-population-insight__apply-filter">
          {!_stateInfo.isLoading ? (
            !isEmpty(_stateInfo.compareOptions) ? (
              <CompareMenu
                isComparePopOverOpen={_stateInfo.isComparePopOverOpen}
                compareOnValue={_stateInfo.compareOnValue}
                compareOptions={get(_stateInfo, "compareOptions", [])}
                openComparePopover={(value: any) => {
                  let _compareOnValue = {};

                  if (value.value === _stateInfo.compareOnValue.value) {
                    _compareOnValue = _stateInfo.compareOnValue;
                  } else {
                    _compareOnValue = value;
                  }
                  _setStateInfo({
                    ..._stateInfo,
                    isComparePopOverOpen: true,
                    compareOnValue: _compareOnValue,
                  });
                }}
                handleCancelClick={() => {
                  _setStateInfo({
                    ..._stateInfo,
                    isComparePopOverOpen: false,
                  });
                }}
                handleChange={(selectedOptions: any) => {
                  _setStateInfo({
                    ..._stateInfo,
                    compareOnValue: {
                      ..._stateInfo.compareOnValue,
                      options: selectedOptions,
                    },
                  });
                }}
                handleApplyClick={() => {
                  if (get(_stateInfo, "compareOnValue.options", []).length > 5) {
                    notification.error({
                      message: "Error",
                      description: "You can only add five values.",
                    });
                  } else if (get(_stateInfo, "compareOnValue.options", []).length > 0) {
                    handleCompareClick(_stateInfo.compareOnValue);
                  } else {
                    notification.error({
                      message: "Error",
                      description: "Please add values from options.",
                    });
                  }
                }}
              />
            ) : null
          ) : (
            <div className="ui-population-insight__apply-filter__compare-preloader">
              <div className="ui-population-insight__apply-filter__compare-preloader__title shimmerBG" />
            </div>
          )}
        </div>

        <div className="ui-population-insight__body">
          <div className="ui-population-insight__body__side-cards">
            {!_stateInfo.isLoading ? (
              !isEmpty(_stateInfo.sideCardData) &&
              map(_stateInfo.sideCardData, (item, index: number) => {
                return (
                  <SideCards
                    key={"sideCard" + index}
                    {...item}
                    handleClick={(e: { stopPropagation: () => void }) => {
                      e.stopPropagation();
                      handleSideCardClick(index);
                    }}
                    activeCard={_stateInfo.sideCardData[_activeSideCardIndex ? _activeSideCardIndex : 0]}
                    colorOfBrand={props.colorOfBrand}
                  />
                );
              })
            ) : (
              <SideCardsPreloader />
            )}
          </div>

          {!_stateInfo.isLoading ? (
            <Reasons
              reasonsOptions={get(_stateInfo.response, "options.reasonsOptions", [])}
              explainabilityInfo={get(_stateInfo, "explainabilityInfo", [])}
              visible={_stateInfo.isPopOverOpen}
              record={_stateInfo.selectedData}
              selectedFilterOptions={_selectedFilterOptions}
              rangeStartDate={_rangeStartDate}
              rangeEndDate={_rangeEndDate}
              activeSideCard={_stateInfo.sideCardData[_activeSideCardIndex ? _activeSideCardIndex : 0]}
              handleClick={() => {
                _setStateInfo({
                  ..._stateInfo,
                  isPopOverOpen: false,
                });
              }}
              title="XplnAble Details"
              style={{
                offset: [get(_stateInfo, "popoverConfig.left", undefined) - 370, get(_stateInfo, "popoverConfig.top", undefined) - 150],
                points: ["bl", "bt"],
              }}
            />
          ) : null}
          {!_stateInfo.isLoading ? (
            <ReportChart
              {..._stateInfo.sideCardData[_activeSideCardIndex ? _activeSideCardIndex : 0]}
              colorOfBrand={props.colorOfBrand[0]}
              yearMonth={_yearMonth}
              endDate={_rangeEndDate}
              handleRowSelect={(record: any, rowIndex: any) => {
                handleTableRowClick(record, rowIndex);
              }}
              handleMarkerSelect={handleMarkerSelect}
              lineGraphInfo={_stateInfo.lineGraphInfo}
              tableGraphInfo={_stateInfo.tableGraphInfo}
              selectedRowIndex={_stateInfo.selectedRowIndex}
              graphConfig={_stateInfo.graphConfig}
              currentGraphType={_stateInfo.currentGraphType}
              barGraphInfo={_stateInfo.barGraphInfo}
              multiLineGraphInfo={_stateInfo.multiLineGraphInfo}
              selectedGraphOptions={_stateInfo.selectedGraphOptions}
              setStateToInitial={setStateToInitial}
              randomColors={get(_stateInfo.response, "options.randomColors", [])}
              graphButtons={get(_stateInfo.response, "options.graphButtons", [])}
              riskAdjustedData={_stateInfo.riskAdjustedData}
              persistentMemberInfo={_stateInfo.persistentMemberInfo}
              riskAndPersistentMemberInfo={_stateInfo.riskAndPersistentMemberInfo}
              handlePersistentAndRiskClick={(selectedValues: any) => {
                let _graphColor = null;
                let _currentGraphType = null;
                let _barGraphInfo = null;

                if (selectedValues.length === 2) {
                  _graphColor = "136,60,228";
                  _currentGraphType = "bar";
                  if(!_stateInfo.riskAndPersistentMemberInfo){
                    _barGraphInfo = null;
                  }
                  else{
                    _barGraphInfo = _stateInfo.riskAndPersistentMemberInfo;
                  }
                } else if (selectedValues.length === 1) {
                  if (head(selectedValues) === "Persistent Members") {
                    _graphColor = "27,190,117";
                    _currentGraphType = "bar";
                    if(!_stateInfo.persistentMemberInfo){
                      _barGraphInfo = null;
                    }
                    else{
                      _barGraphInfo = _stateInfo.persistentMemberInfo;
                    }
                  } else {
                    _graphColor = "239,109,42";
                    _currentGraphType = "bar";
                    if(!_stateInfo.riskAdjustedData){
                      _barGraphInfo = null;
                    }
                    else{
                      _barGraphInfo = _stateInfo.riskAdjustedData;
                    }
                  }
                } else if (isEmpty(selectedValues)) {
                  _currentGraphType = "scatter";
                  _barGraphInfo = [];
                }

                _setStateInfo({
                  ..._stateInfo,
                  graphConfig: {
                    graphColor: _graphColor,
                  },
                  currentGraphType: _currentGraphType,
                  barGraphInfo: _barGraphInfo,
                  selectedGraphOptions: selectedValues,
                });
              }}
              openPopover={(x: any, y: any, record: any) => {
                _setStateInfo({
                  ..._stateInfo,
                  isPopOverOpen: !_stateInfo.isPopOverOpen,
                  selectedData: record,
                  popoverConfig: {
                    left: x,
                    top: y,
                  },
                });
              }}
            />
          ) : (
            <div className="popInst-report-loader">
              <GraphPreloader />
              <TablePreloader />
            </div>
          )}
        </div>
      </div>
      <Drawer
        destroyOnClose={true}
        title={
          <div className="ui-population-insight__drawer-filter">
            <div className="ui-population-insight__drawer-filter__title">Filters:</div>
            <div className="ui-population-insight__drawer-filter__sub-title clr-e100 f12">
              <Icon name="info" colorOfBrand="#e75547"></Icon>&nbsp;Applicable on {getYearMonth(_filteryearMonth.current?.endDate)}
            </div>
          </div>
        }
        placement="right"
        closable={false}
        onClose={() =>
          _setStateInfo({
            ..._stateInfo,
            isOpenDrawer: false,
          })
        }
        visible={_stateInfo.isOpenDrawer}
        key="right"
        width={375}
        bodyStyle={{ padding: 0 }}
        footer={
          <div style={{ textAlign: "right" }} className="cancel-button">
            <Button
              type="text"
              size="large"
              onClick={() => {
                _setStateInfo({
                  ..._stateInfo,
                  isOpenDrawer: false,
                });
              }}
            >
              Cancel
            </Button>

            <Button
              type="primary"
              onClick={(e) => {
                e.stopPropagation();
                applySideFilters(undefined, undefined, undefined);
                setDisplayMessage(false);
              }}
              className="apply-filter"
              style={{ height: "40px" }}
            >
              Apply Filter
            </Button>
          </div>
        }
        extra={
          <Button type="link" onClick={clearFilterHandler} className="clr-e100" style={{ fontWeight: "normal" }}>
            Clear All
          </Button>
        }
        className="ui-population-insight__filter_drawer"
      >
        <Filters data={_filtersData} changeHandler={filtersChangeHandler} />
      </Drawer>

      {_stateInfo.isNoDataFoundModalVisible ? info(() => _setStateInfo({ ..._stateInfo, isNoDataFoundModalVisible: false, isOpenDrawer: true })) : null}
    </>
  );
};

/* -------------------------------------------- */

const getYearMonth = (yearMonth?: any) => {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const date = yearMonth + "-01";
  const d = new Date(date);
  return `${months[d.getMonth()]} ${d.getFullYear()}`;
};

const Cards = (props: any) => {
  return (
    <div className="ui-population-insight__filter__card">
      <div className="ui-population-insight__filter__card__icon">
        <Icon name={props.icon} colorOfBrand={props.colorOfBrand[0]} />
      </div>

      <div className="ui-population-insight__filter__card__info">
        <div className="ui-population-insight__filter__card__info__body">
          <h3 className="ui-population-insight__filter__card__info__body__title">
            {props.title} {props.title.includes("Total Active Members") ? <span className="sub-label">(as of {getYearMonth(props.cardYearMonth)})</span> : ""}
          </h3>
          <div className="ui-population-insight__filter__card__info__body__value">
            {props.icon === "dollar" ? "$ " : ""}
            {get(props, "value", 0) ? (props.title.includes("Last 12 Months Cost") ? props.lastMonthCost?.toLocaleString() : (+props.value)?.toLocaleString()) : 0}
          </div>
        </div>

        {!isNull(props.previousValue) && (
          <div className="ui-population-insight__filter__card__info__extra-info">
            <i className="ui-population-insight__filter__card__info__extra-info__previous-val">In Previous Month : {get(props, "previousValue", 0) ? props.previousValue?.toLocaleString() : 0}</i>
            <PercentageNormal percentage={props.percentage} />
          </div>
        )}
        {props.icon === "dollar" && (
          <div className="ui-population-insight__filter__card__info__extra-info">
            <i className="ui-population-insight__filter__card__info__extra-info__previous-val">{props.date}</i>
          </div>
        )}
      </div>
    </div>
  );
};

/* -------------------------------------------- */

export const CardsPreloader = (props: any) => {
  return (
    <>
      {map([1, 2, 3], (item) => {
        return (
          <div key={item} className="ui-population-insight__filter__card-preloader">
            <div className="ui-population-insight__filter__card-preloader__icon shimmerBG"></div>
            <div className="ui-population-insight__filter__card-preloader__info">
              <div className="ui-population-insight__filter__card-preloader__info__body">
                <div className="ui-population-insight__filter__card-preloader__info__body__title shimmerBG"></div>
                <div className="ui-population-insight__filter__card-preloader__info__body__value shimmerBG"></div>
                <div className="ui-population-insight__filter__card-preloader__info__body__extra-info shimmerBG"></div>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

/* -------------------------------------------- */

const SideCards = (props: any) => {
  const activeCardStyle = {
    border: `1px solid #4ECFB9`,
    // boxShadow: `0 0 8px ${props.colorOfBrand[1]}`,
    boxShadow: `0 0 8px #4ECFB9`,
  };
  const activeTitleStyle = {
    color: `${props.colorOfBrand[1]}`,
  };

  return (
    <div className="side-card" onClick={props.handleClick} style={props.activeCard.title === props.title ? activeCardStyle : undefined}>
      <div className="side-card__graph">
        <Chart
          options={{
            xaxis: {
              categories: props.areaChart.monthYear,
              labels: {
                show: false,
              },
              axisTicks: {
                show: false,
              },
              tooltip: {
                enabled: false,
              },
            },
            yaxis: {
              labels: {
                show: false,
              },
              axisTicks: {
                show: false,
              },
              tooltip: {
                enabled: false,
              },
            },
            stroke: {
              curve: "smooth",
              width : 0.2
            },
            grid: {
              show: false,
              padding: {
                top: 0,
                right: 0,
                bottom: -8,
                left: -8,
              },
            },
            chart: {
              zoom: {
                enabled: false,
              },
              type: "area",
              toolbar: {
                show: false,
              },
            },
            legend: {
              show: false,
            },
            markers: {
              size: 0,
            },
            fill: {
              type: "gradient",
              gradient: {
                shade: "light",
                type: "vertical",
                shadeIntensity: 0.5,
                opacityFrom: 0.7,
                opacityTo: 0.3,
                stops: [0, 100],
              },
            },
            colors: ["#4ECFB9"],
            tooltip: {
              intersect: true,
              shared: false,
            },
          }}
          series={[
            {
              type: "area",
              data: props.areaChart.currentMonthYearValues,
            },
          ]}
          type="line"
          height="100%"
          width= "100%"
        />
      </div>
      <div className="side-card__container">
        <div className="side-card__container__head">
          <h3 className="side-card__container__head__title" style={props.activeCard.title === props.title ? activeTitleStyle : undefined}>
            {props.title}
          </h3>
          <div style={{ display: "flex" }}>
            <Percentage percentage={props.percentage} />
            <div className="side-card__container__head__icon">
              {props.activeCard.title === props.title ? (
                <Tooltip placement="top" title={props.tooltipText}>
                  <div>
                    <Icon name={"info"} colorOfBrand={props.colorOfBrand[1]} />
                  </div>
                </Tooltip>
              ) : (
                <Icon name={"open"} colorOfBrand={props.colorOfBrand[0]} />
              )}
            </div>
          </div>
        </div>
        <div className="side-card__container__sub-title" style={{ color: props.colorOfBrand[0] }}>
          {props.subTitle}
        </div>
        <div className="side-card__container__date-filter">
          {map(props.dateFilter, (item) => {
            return (
              <div key={item.date} className="ui-population-insight__body__side-cards__card__date-filter__body">
                <div className="ui-population-insight__body__side-cards__card__date-filter__body__date">{item.date}</div>
                <div className="ui-population-insight__body__side-cards__card__date-filter__body__value">
                  {props.title === "PMPM" ? <>$</> : null}
                  {props.title === "HCC Risk" ? (get(item, "value", 0) ? item.value.toLocaleString() : 0) : get(item, "value", 0) ? item.value.toLocaleString() : 0}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------- */

const SideCardsPreloader = (props: any) => {
  return (
    <>
      {map([1, 2, 3, 4, 5], (item) => {
        return (
          <div key={item} className="ui-population-insight__body__side-cards__card-preloader">
            <div className="ui-population-insight__body__side-cards__card-preloader__head">
              <div className="ui-population-insight__body__side-cards__card-preloader__head__title shimmerBG"></div>
              <div className="ui-population-insight__body__side-cards__card-preloader__head__icon shimmerBG"></div>
            </div>
            <div className="ui-population-insight__body__side-cards__card-preloader__sub-title shimmerBG"></div>
            <div className="ui-population-insight__body__side-cards__card-preloader__date-filter">
              <div className="ui-population-insight__body__side-cards__card-preloader__date-filter__body">
                <div className="ui-population-insight__body__side-cards__card-preloader__date-filter__body__date shimmerBG"></div>
                <div className="ui-population-insight__body__side-cards__card-preloader__date-filter__body__value shimmerBG"></div>
              </div>
              <div className="ui-population-insight__body__side-cards__card-preloader__date-filter__body">
                <div className="ui-population-insight__body__side-cards__card-preloader__date-filter__body__date shimmerBG"></div>
                <div className="ui-population-insight__body__side-cards__card-preloader__date-filter__body__value shimmerBG"></div>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

/* -------------------------------------------- */

const ReportChart = (props: any) => {
  const color = props.colorOfBrand;

  const _randomColors = get(props, "randomColors", []);

  // data for multilineGraph
  const dataForGraph: {}[] = [];
  let _graphData: any = [];

  const getDataPoints = (category: any, pointLabel: any, points: any) => {
    let dataPoints = [];
    dataPoints = category.map((item: any) => {
      if (pointLabel.indexOf(item) >= 0) {
        return points[pointLabel.indexOf(item)];
      } else {
        return null;
      }
    });
    return dataPoints;
  };

  const scatterLineGraphInfo = () => {
    let tempObj = {};
    let tempXaxis: any = [];
    let series = [];
    let plotOptions = {};
    if (props?.lineGraphInfo?.monthYearLabel && props?.lineGraphInfo?.projectionMonthYearLabel) {
      tempXaxis = [...props?.lineGraphInfo?.monthYearLabel, ...props?.lineGraphInfo?.projectionMonthYearLabel];
      tempXaxis = tempXaxis.filter((item: any, index: any) => tempXaxis.indexOf(item) === index);
    } else {
      if (props?.lineGraphInfo?.monthYearLabel) {
        tempXaxis = [...props?.lineGraphInfo?.monthYearLabel];
      }
    }

    if (props?.lineGraphInfo?.monthYearLabel && props?.lineGraphInfo?.projectionMonthYearLabel) {
      series = [
        {
          name: "Previous Year Month",
          type: "area",
          data: getDataPoints(tempXaxis, props?.lineGraphInfo?.monthYearLabel, props?.lineGraphInfo?.previousMonthYearValues),
        },
        {
          name: "Predicted (Next three months)",
          type: "area",
          data: getDataPoints(tempXaxis, props?.lineGraphInfo?.projectionMonthYearLabel, props?.lineGraphInfo?.projection),
        },
        {
          name: "Current Year Month",
          type: "area",
          data: getDataPoints(tempXaxis, props?.lineGraphInfo?.monthYearLabel, props.lineGraphInfo?.currentMonthYearValues),
        },
      ];
      plotOptions = {
        // colors: ["#00e393", "#ffaf20", "#0b96fb"],
        colors: ["#81C784", "#ffaf20", "#0A96D4"],
        stroke: {
          width: [2, 2, 2],
          dashArray: [5, 5, 0],
          curve: "smooth",
        },
        markers: {
          size: [5, 5, 7],
          shape: ["square", "square", "circle"],
        },
      };
    } else {
      series = [
        {
          name: "Previous Year Month",
          type: "area",
          data: getDataPoints(tempXaxis, props?.lineGraphInfo?.monthYearLabel, props?.lineGraphInfo?.previousMonthYearValues),
        },
        {
          name: "Current Year Month",
          type: "area",
          data: getDataPoints(tempXaxis, props?.lineGraphInfo?.monthYearLabel, props.lineGraphInfo?.currentMonthYearValues),
        },
      ];
      plotOptions = {
         colors: ["#81C784", "#0A96D4"],
          stroke: {
            width: [2, 2],
            dashArray: [5, 0],
            curve: "smooth", 
          },
           markers: {
             size: [5, 7],
            shape: ["square", "circle"] 
          },
         }
    }

    tempObj = {
      categories: tempXaxis,
      series,
      extendedProjection: props.lineGraphInfo.extendedProjection,
      plotOptions,
    };

    return tempObj;
  };

  const barGraphInfo = () => {
    let tempObj = {};
    let series;
    let tempXaxis: any = [];
    let color: any = [];
    console.log(props.barGraphInfo);
    if(props?.barGraphInfo){
      if (props?.barGraphInfo?.projection) {
        tempXaxis = [...props?.barGraphInfo?.monthYearLabel, ...props?.barGraphInfo?.projectionMonthYearLabel];
        tempXaxis = tempXaxis.filter((item: any, index: any) => tempXaxis.indexOf(item) === index);
        series = [
          {
            name: "Current Period",
            type: "bar",
            data: getDataPoints(tempXaxis, props?.barGraphInfo?.monthYearLabel, props.barGraphInfo?.currentMonthYearValues),
          },
          {
            name: "Previous Period",
            type: "bar",
            data: getDataPoints(tempXaxis, props?.barGraphInfo?.monthYearLabel, props.barGraphInfo?.previousMonthYearValues),
          },
          {
            name: "Predicted (Next three months)",
            type: "bar",
            data: getDataPoints(tempXaxis, props?.barGraphInfo?.projectionMonthYearLabel, props.barGraphInfo?.projection),
          },
        ];
        color = ["#1bbe75", "#8ddeba", "#d1f2e3"];
      } else {
        tempXaxis = [...props?.barGraphInfo?.monthYearLabel];
        series = [
          {
            name: "Current Period",
            type: "bar",
            data: [...props.barGraphInfo?.currentMonthYearValues],
          },
          {
            name: "Previous Period",
            type: "bar",
            data: [...props.barGraphInfo?.previousMonthYearValues],
          },
        ];
        color = props?.selectedGraphOptions?.length === 2 ? ["#883ce4", "#c39df1"] : ["#ef6d2a", "#f7b694"];
      }
      tempObj = {
        categories: tempXaxis,
        series,
        color,
        extendedProjection: props.barGraphInfo.extendedProjection,
      };
      return tempObj;
    }
    return null;
    
  };
  const multiLineGraphInfo = () => {
    let tempObj = {};
    let series: any = [];
    let tempXaxis: any = [];
    let color: any = [];
    const { multiLineGraphInfo } = props;

    for(let i =0; i<props.multiLineGraphInfo.length; i++){
      if(props.multiLineGraphInfo[i]){
        tempXaxis = [...tempXaxis, ...props.multiLineGraphInfo[i].monthYearLabel];
      }
    }
    tempXaxis = tempXaxis.filter((item: any, index: any) => tempXaxis.indexOf(item) === index);
    console.log(tempXaxis);

    for(let i =0; i < multiLineGraphInfo.length; i++){
      if(multiLineGraphInfo[i]){
        let temp =         {
          name: multiLineGraphInfo[i].subType,
          type: "area",
          data: getDataPoints(tempXaxis, multiLineGraphInfo[i].monthYearLabel, multiLineGraphInfo[i].currentMonthYearValues),
        };
        series.push(temp);
      }
    }
    let plotOptions = {
       stroke: {
         width: 2,
         curve: "smooth", 
       },
        markers: {
          size: 5,
         shape: "circle"
       },
      }
    console.log(series);
    tempObj = {
      categories: tempXaxis,
      series,
      plotOptions,
    };
    return tempObj;
  }



  // const multiLineGraph = () => {
  //   let tempObj = {};
  //   let series;
  //   let tempXaxis: any = [];
  //   let color: any = [];
  // }

  if (props.currentGraphType === "scatter") {
    _graphData = scatterLineGraphInfo();
  }
  if (props.currentGraphType === "bar") {
    _graphData = barGraphInfo();
  }
  if (props.currentGraphType === "multiLineGraph") {
    console.log(props.multiLineGraphInfo, props.currentGraphType);
    _graphData = multiLineGraphInfo();
    _graphData["extendedProjection"] = get(props, "lineGraphInfo.extendedProjection", []);

    // _graphData = props.multiLineGraphInfo;
    // _graphData["extendedProjection"] = get(props, "lineGraphInfo.extendedProjection", []);

    // forEach(_graphData, (info, index: number) => {
    //   dataForGraph.push({
    //     type: "scatter",
    //     mode: "lines+markers",
    //     x: get(info, "monthYearLabel", []),
    //     y: get(info, "currentMonthYearValues", []),
    //     name: get(info, "subType", []),
    //     line: {
    //       color: get(_randomColors[index], "color", undefined),
    //     },
    //     hovertemplate: `%{x}<br> %{y:${props.title === "HCC Risk" ? ".3f" : ".2f"}}`,
    //     marker: {
    //       size: 12,
    //     },
    //   });
    //   dataForGraph.push({
    //     x: get(info, "projectionMonthYearLabel", []),
    //     y: get(info, "projection", []),
    //     mode: "lines",
    //     type: "scatter",
    //     line: {
    //       dash: "dash",
    //       color: get(_randomColors[index], "color", undefined),
    //     },
    //     name: `Predicted (Next three months)`,
    //     hovertemplate: `%{x}<br> %{y:${props.title === "HCC Risk" ? ".3f" : ".2f"}}`,
    //   });
    // });
  }

  const markerSelectionEventHandler = (event: any, chartContext: any, config: any) => {
    let record: any = {};
    if (config?.seriesIndex === 2 && props?.lineGraphInfo?.projectionMonthYearLabel) {
      record.y = props?.lineGraphInfo?.currentMonthYearValues[config.dataPointIndex];
      record.x = props?.lineGraphInfo?.monthYear[config.dataPointIndex];

      if (event.button === 2) {
        // right click handling
        event.preventDefault();
        props.openPopover(event.pageX, event.pageY, record);
        return false;
      } else {
        // left click handling
        event.stopPropagation();
        console.log(event.pageX, event.pageY, record);
        props.handleMarkerSelect(record);
      }
    } else if (config?.seriesIndex === 1 && !props?.lineGraphInfo?.projectionMonthYearLabel) {
      record.y = props?.lineGraphInfo?.currentMonthYearValues[config.dataPointIndex];
      record.x = props?.lineGraphInfo?.monthYear[config.dataPointIndex];

      if (event.button === 2) {
        // right click handling
        event.preventDefault();
        props.openPopover(event.pageX, event.pageY, record);
        return false;
      } else {
        // left click handling
        event.stopPropagation();
        console.log(event.pageX, event.pageY, record);
        props.handleMarkerSelect(record);
      }
    }
  };

  // layout
  const layout = {
    legend: {
      x: 0,
      y: -0.2,
      font: {
        size: 14,
        color: "#7A869A",
        family: "Fira Sans",
      },
    },
    xaxis: {
      showgrid: false,
      zeroline: true,
      showline: true,
      linecolor: "#F4F5F7",
      autotick: false,
      ticklen: 8,
      tickwidth: 0.5,
      tickcolor: "#DFE1E6",
      tickfont: {
        size: 14,
        color: "#B3BAC5",
        family: "Fira Sans",
      },
    },
    yaxis: {
      zeroline: true,
      showline: true,
      linecolor: "#F4F5F7",
      gridcolor: "#F4F5F7",
      tickfont: {
        size: 14,
        color: "#7A869A",
        family: "Fira Sans",
      },
    },
    margin: {
      l: 40,
      r: 40,
      t: 40,
      b: 0,
    },
  };
  return (
    <div className="ui-population-insight__body__report">
      <div
        className="ui-population-insight__body__report__graph"
        onContextMenu={(e) => {
          e.preventDefault();
          return false;
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {props.currentGraphType === "multiLineGraph" ? (
            <div className="ui-population-insight__body__report__graph__compare-title">
              <div className="ui-population-insight__body__report__graph__compare-title__back" onClick={() => props.setStateToInitial({ forcefullyChange: true })}>
                <Icon name="back" />
              </div>
              <div className="ui-population-insight__body__report__graph__compare-title__title" onClick={() => props.setStateToInitial({ forcefullyChange: true })}>
                {props.reportTitle} /
              </div>
              <div className="ui-population-insight__body__report__graph__compare-title__compare">Compare All</div>
            </div>
          ) : (
            <div className="ui-population-insight__body__report__graph__title">
              {`${props.reportTitle} `}
              {!["PMPM", "HCC Risk"].includes(props.reportTitle) ? <span className="sub-label_col-label">({props.subTitle})</span> : null}
            </div>
          )}
          <div className="ui-population-insight__body__report__graph__sub-head__right">
            <Checkbox.Group
              style={{ width: "100%" }}
              onChange={(checkedValues: any) => {
                props.handlePersistentAndRiskClick(checkedValues);
              }}
              value={props.selectedGraphOptions}
            >
              {map(get(props, "graphButtons", []), (item, index) => {
                return (
                  <Fragment key={item + index}>
                    {item.title === "Risk Adjusted" && !isEmpty(props.riskAdjustedData) ? (
                      <Checkbox disabled={props.currentGraphType === "multiLineGraph"} key={item.title} value={`${item.title}`}>
                        {item.title}
                      </Checkbox>
                    ) : null}

                    {item.title !== "Risk Adjusted" && !milleniumClientCheck() ? (
                      <Checkbox disabled={props.currentGraphType === "multiLineGraph"} key={item.title} value={`${item.title}`}>
                        {item.title}
                      </Checkbox>
                    ) : null}
                  </Fragment>
                );
              })}
            </Checkbox.Group>
          </div>
        </div>
        {/* <div className="ui-population-insight__body__report__graph__sub-head">
          {props.reportTitle !== "HCC Risk" && props?.yearMonth === props?.endDate && (
            <div className="ui-population-insight__body__report__graph__sub-head__left">
              <div className="ui-population-insight__body__report__graph__sub-head__left__title">Projected values as of latest month ({getYearMonth(props.yearMonth)}):</div>
              <div className="ui-population-insight__body__report__graph__sub-head__left__title">
                6 months&nbsp;-&nbsp;
                <div className="w700">
                  {props.reportTitle === "PMPM" ? <>$ </> : null} {get(_graphData, "extendedProjection.sixMonths", 0)?.toLocaleString()}
                </div>
              </div>
              <div className="ui-population-insight__body__report__graph__sub-head__left__title">
                9 months&nbsp;-&nbsp;
                <div className="w700">
                  {props.reportTitle === "PMPM" ? <>$ </> : null} {get(_graphData, "extendedProjection.nineMonths", 0)?.toLocaleString()}
                </div>
              </div>
              <div className="ui-population-insight__body__report__graph__sub-head__left__title">
                12 months&nbsp;-&nbsp;
                <div className="w700">
                  {props.reportTitle === "PMPM" ? <>$ </> : null} {get(_graphData, "extendedProjection.twelveMonths", 0)?.toLocaleString()}
                </div>
              </div>
            </div>
          )}
        </div> */}
        <div className="ui-population-insight__body__report__graph__sub-head">
          {props.reportTitle !== "HCC Risk" && !milleniumClientCheck() && props?.yearMonth === props?.endDate && (
            <div className="ui-population-insight__body__report__graph__sub-head__left">
              <div className="ui-population-insight__body__report__graph__sub-head__left__title">Projected values as of latest month ({getYearMonth(props.yearMonth)}):</div>
              <div className="ui-population-insight__body__report__graph__sub-head__left__title">
                6 months&nbsp;-&nbsp;
                <div className="w700">
                  {props.reportTitle === "PMPM" ? <>$ </> : null} {get(_graphData, "extendedProjection.sixMonths", 0)?.toLocaleString()}
                </div>
              </div>
              <div className="ui-population-insight__body__report__graph__sub-head__left__title">
                9 months&nbsp;-&nbsp;
                <div className="w700">
                  {props.reportTitle === "PMPM" ? <>$ </> : null} {get(_graphData, "extendedProjection.nineMonths", 0)?.toLocaleString()}
                </div>
              </div>
              <div className="ui-population-insight__body__report__graph__sub-head__left__title">
                12 months&nbsp;-&nbsp;
                <div className="w700">
                  {props.reportTitle === "PMPM" ? <>$ </> : null} {get(_graphData, "extendedProjection.twelveMonths", 0)?.toLocaleString()}
                </div>
              </div>
            </div>
          )}
        </div>

        {props.currentGraphType === "scatter" ? (
          <div className="custom-plot">
            <Chart
              options={{
                chart: {
                  animations: {
                    enabled: false,
                    easing: "easein",
                    speed: 800,
                    animateGradually: {
                      enabled: true,
                      delay: 5,
                    },
                    dynamicAnimation: {
                      enabled: true,
                      speed: 150,
                    },
                  },
                  toolbar: {
                    show: false,
                  },
                  events: {
                    dataPointSelection: function (event, chartContext, config) {
                      markerSelectionEventHandler(event, chartContext, config);
                    },
                  },
                },
                xaxis: {
                  categories: _graphData?.categories,
                },
                grid: {
                  show: false,
                },
                tooltip: {
                  enabled: true,
                  intersect: false,
                  shared: true,
                  // custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                  //   {console.log(series, seriesIndex, dataPointIndex, w ,"series")}
                  //   return (
                  //     '<div class="arrow_box">' + "<div> Previous Month:" + series[0][dataPointIndex] + "</div>" + "<div> Current Month:" + series[seriesIndex][dataPointIndex] + "</div>" + "</div>"
                  //   );
                  // },
                },
                fill: {
                  type: "gradient",
                  gradient: {
                    shade: "light",
                    type: "vertical",
                    shadeIntensity: 0.5,
                    opacityFrom: 0.6,
                    opacityTo: 0.2,
                    stops: [0, 100],
                  },
                },
                ..._graphData?.plotOptions,
              }}
              series={_graphData?.series}
              type="line"
              height="460px"
            />
          </div>
        ) : null}
        {props.currentGraphType === "bar" ? (
          <div className="custom-plot">
            <Chart
              options={{
                chart: {
                  animations: {
                    enabled: false,
                    easing: "easeout",
                    speed: 800,
                    animateGradually: {
                      enabled: true,
                      delay: 150,
                    },
                    dynamicAnimation: {
                      enabled: true,
                      speed: 150,
                    },
                  },
                  toolbar: {
                    show: false,
                  },
                },
                plotOptions: {
                  bar: {
                    borderRadius: 8,
                  },
                },
                grid: {
                  show: false,
                },
                xaxis: {
                  categories: _graphData?.categories,
                },
                stroke: {
                  curve: "smooth",
                },

                markers: {
                  size: 7,
                },

                fill: {
                  type: "solid",
                  opacity: 1,
                  gradient: {
                    shade: "dark",
                    type: "vertical",
                  },
                },
                colors: _graphData?.color,
              }}
              series={_graphData?.series}
              type="line"
              height="460px"
            />
          </div>
        ) : null}
        {props.currentGraphType === "multiLineGraph" ? (
          <Chart
              options={{
                chart: {
                  animations: {
                    enabled: false,
                    easing: "easein",
                    speed: 800,
                    animateGradually: {
                      enabled: true,
                      delay: 5,
                    },
                    dynamicAnimation: {
                      enabled: true,
                      speed: 150,
                    },
                  },
                  toolbar: {
                    show: false,
                  },
                },
                xaxis: {
                  categories: _graphData?.categories,
                },
                grid: {
                  show: false,
                },
                tooltip: {
                  intersect: false,
                  shared: true,
                },

                fill: {
                  type: "gradient",
                  gradient: {
                    shade: "light",
                    type: "vertical",
                    shadeIntensity: 0.5,
                    opacityFrom: 0.6,
                    opacityTo: 0.2,
                    stops: [0, 100],
                  },
                },
                ..._graphData?.plotOptions,
              }}
              series={_graphData?.series}
              type="line"
              height="460px"
            />
        ) : null}
      </div>
      <div className="ui-population-insight__body__report__table">
        <Table
          dataSource={props.tableGraphInfo}
          bordered={true}
          tableLayout="fixed"
          columns={tableColumns}
          pagination={{ defaultPageSize: 12 }}
          locale={TableLocale}
          onRow={(record, rowIndex) => {
            return {
              className: props.selectedRowIndex === rowIndex ? `ui-population-insight__body__report__table__row--selected` : undefined,
              onClick: (e) => {
                e.stopPropagation();
                props.handleRowSelect(record, rowIndex);
              },
            };
          }}
          // As there is no use of showing the total for avg hcc risk and other admission columns, so commenting the code for
          //  the total row in the table

          // summary={(_tableGraphInfo) => {
          //   let _patientEnrollCount = 0;
          //   let _avgHccRisk = 0;
          //   let _totalCost = 0;
          //   let _hospitalAdmissions = 0;
          //   let _reAdmissions = 0;
          //   let _erAdmissions = 0;
          //   let _erVisits = 0;
          // summary={(_tableGraphInfo) => {
          //   let _patientEnrollCount = 0;
          //   let _avgHccRisk = 0;
          //   let _totalCost = 0;
          //   let _hospitalAdmissions = 0;
          //   let _reAdmissions = 0;
          //   let _erAdmissions = 0;
          //   let _erVisits = 0;

          //   _tableGraphInfo.forEach(({ erVisits, patientEnrollCount, avgHccRisk, totalCost, hospitalAdmissions, reAdmissions, erAdmissions }) => {
          //     _erVisits += Number(erVisits);
          //     _patientEnrollCount += Number(patientEnrollCount);
          //     _avgHccRisk += Number(avgHccRisk);
          //     _totalCost += Number(totalCost);
          //     _hospitalAdmissions += Number(hospitalAdmissions);
          //     _reAdmissions += Number(reAdmissions);
          //     _erAdmissions += Number(erAdmissions);
          //   });

          //   return (
          //   <></>
          //

          //   //   <Table.Summary.Row>
          //   //     <Table.Summary.Cell className="textAlignLeft" index={1}>
          //   //       Total
          //   //     </Table.Summary.Cell>
          //   //     <Table.Summary.Cell className="textAlignRight" index={2}>
          //   //       {_patientEnrollCount.toLocaleString()}
          //   //     </Table.Summary.Cell>
          //   //     <Table.Summary.Cell className="textAlignRight" index={3}>
          //   //       {_avgHccRisk.toLocaleString()}
          //   //     </Table.Summary.Cell>
          //   //     <Table.Summary.Cell className="textAlignRight" index={4}>
          //   //       $ {_totalCost.toLocaleString()}
          //   //     </Table.Summary.Cell>
          //   //     <Table.Summary.Cell className="textAlignRight" index={5}>
          //   //       {_hospitalAdmissions.toFixed(2).toLocaleString()}
          //   //     </Table.Summary.Cell>
          //   //     <Table.Summary.Cell className="textAlignRight" index={6}>
          //   //       {_reAdmissions.toFixed(2).toLocaleString()}
          //   //     </Table.Summary.Cell>
          //   //     <Table.Summary.Cell className="textAlignRight" index={7}>
          //   //       {_erAdmissions.toFixed(2).toLocaleString()}
          //   //     </Table.Summary.Cell>
          //   //     <Table.Summary.Cell className="textAlignRight" index={8}>
          //   //       {_erVisits.toFixed(2).toLocaleString()}
          //   //     </Table.Summary.Cell>
          //   //   </Table.Summary.Row>
          //   );
          // }}
        />
      </div>
    </div>
  );
};

/* -------------------------------------------- */

const TablePreloader = () => {
  return (
    <div className="ui-table-preloader">
      {map([1, 2, 3, 4, 5, 6], (item) => {
        return (
          <div className="ui-table-preloader__row" key={"row_" + item}>
            {map([1, 2, 3, 4, 5, 6], (item) => {
              return (
                <div key={"col_" + item} className="ui-table-preloader__row__column">
                  <div className="ui-table-preloader__row__column__title shimmerBG"></div>
                  <div className="ui-table-preloader__row__column__sub-title shimmerBG"></div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

/* -------------------------------------------- */
const GraphPreloader = () => {
  return (
    <div className="ui-graph-preloader">
      <div className="ui-graph-preloader__title shimmerBG"></div>
    </div>
  );
};

/** ------------------------------------------- */

const CompareMenu = (props: any) => {
  const inputRef = React.useRef(null);
  const _compareOptions = get(props, "compareOptions", []);
  const _item = find(_compareOptions, {
    value: get(props, "compareOnValue.value", null),
  });

  const _content = (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Checkbox.Group
        className="ui-population-insight__apply-filter__compare__sub-option"
        value={get(props, "compareOnValue.options", [])}
        onChange={(checkedValues: any) => {
          props.handleChange(checkedValues);

          // disable the other options if 5 options are selected
          forEach(_item.options, (option) => {
            if (!includes(checkedValues, option.value) && checkedValues.length >= 5) {
              option.disable = true;
            } else {
              option.disable = false;
            }
          });
        }}
      >
        {!isEmpty(_item) && !isUndefined(_item)
          ? map(_item.options, (item) => {
              return (
                <Checkbox key={item.value} className="ui-population-insight__apply-filter__compare__sub-option__title" value={item.value} disabled={item.disable || item.colorFlag}>
                  <span style={item.colorFlag ? { color: "darkGrey" } : undefined}>{item.title}</span>
                </Checkbox>
              );
            })
          : null}

        <div className="ui-population-insight__apply-filter__compare__sub-option__warning">
          <div className="ui-population-insight__apply-filter__compare__sub-option__warning__icon">
            <Icon name="alert-minor" />
          </div>
          <div className="ui-population-insight__apply-filter__compare__sub-option__warning__title">You can only add 5 values</div>
        </div>

        <div style={{ textAlign: "right" }} className="ui-population-insight__apply-filter__compare__sub-option__buttons">
          <Button
            className="cancel-btn"
            size="large"
            onClick={(e) => {
              e.stopPropagation();
              // const _ref: any = inputRef;
              // get(_ref, "current", null)?.click();
              props.handleCancelClick();
            }}
          >
            Cancel
          </Button>

          <Button
            type="primary"
            onClick={(e) => {
              e.stopPropagation();
              const _ref: any = inputRef;
              get(_ref, "current", null)?.click();
              props.handleApplyClick();
            }}
            className="apply-filter"
            style={{ height: "40px" }}
          >
            Apply
          </Button>
        </div>
      </Checkbox.Group>
    </div>
  );
  const content = (
    <Popover visible={props.isComparePopOverOpen} content={_content} placement="left" trigger="click" overlayClassName="ui-population-insight__overlay">
      {map(_compareOptions, (option) => {
        return (
          <div
            key={option.value}
            className="ui-population-insight__apply-filter__compare__option"
            onClick={(e) => {
              e.stopPropagation();
              props.openComparePopover({ value: option.value });
            }}
          >
            <div className="ui-population-insight__apply-filter__compare__option__icon">
              <Icon name={"arrowLeft"} />
            </div>
            <div className="ui-population-insight__apply-filter__compare__option__title">{option.title}</div>
          </div>
        );
      })}
    </Popover>
  );

  return (
    <Popover content={content} placement="bottom" trigger="click" overlayClassName="ui-population-insight__overlay">
      <div className="ui-population-insight__apply-filter__compare" ref={inputRef}>
        <div className="ui-population-insight__apply-filter__compare__title">Compare</div>
        <div className="ui-population-insight__apply-filter__compare__icon">
          <Icon name={"arrowDown"} />
        </div>
      </div>
    </Popover>
  );
};

const info = (action: () => void) => {
  Modal.info({
    title: "NO DATA FOUND",
    maskClosable: true,
    content: (
      <div>
        <p>Please choose other filters.</p>
      </div>
    ),
    onOk() {
      action();
    },
  });
};

export default PopulationInsight;
